import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { QueryErrorResetBoundary, useQuery } from "@tanstack/react-query";
import { useFlag } from "@unleash/proxy-client-react";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate } from "react-router-dom";

import { Callout } from "@/common/components/callout/callout";
import { Chart } from "@/common/components/chart/chart";
import { Failed, NoData } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import { PageTitle } from "@/common/components/page-title/page-title";
import { QuickFilters } from "@/common/components/quick-filters/quick-filters";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useScrollbarToggle } from "@/common/hooks/use-scrollbar-toggle";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";

import { TwoColumnLayout } from "../layout/layout";
import { usePostalCodesQuery } from "../shared/api/get-postal-codes";
import {
  PostalCodeDialog,
  usePostalCodeDialogParams,
} from "../shared/components/postal-code-dialog/postal-code-dialog";
import { PostalCodeInfoBox } from "../shared/components/postal-code-dialog/postal-code-info-box";
import { usePostalCodeObject } from "../shared/components/postal-code-dialog/use-postal-code-object";
import { predictionQuery } from "./yield-predictions.api";
import { PredictionChart } from "./yield-predictions.chart";
import { useYearFilters } from "./yield-predictions.hooks";
import { CropConfig, crops, path } from "./yield-predictions.utils";

const YieldPredictions = () => {
  const { _ } = useLingui();
  const sidebar = useSidebar();
  const { params } = usePostalCodeDialogParams();
  useScrollbarToggle(params?.enforceDialog);
  const postalCodeDialog = usePostalCodeDialogParams();
  const postalCodes = useQuery(usePostalCodesQuery());
  const showCopyright = useFlag("cfd-9151-yield-predictions-copyright");
  const postalCodeObject = usePostalCodeObject(
    postalCodeDialog.params?.postalCode,
  );

  if (postalCodes.status === "success" && params?.postalCode) {
    if (
      !postalCodes.data.find(
        (postalCode) => postalCode.code === params.postalCode,
      )
    ) {
      return <Navigate to="?" />;
    }
  }

  return (
    <TwoColumnLayout
      left={{
        content: (
          <>
            <div className="flex basis-full flex-col">
              <Metadata title={_(msg`Predikce výnosů`)} />
              <PostalCodeInfoBox
                code={postalCodeObject.code}
                name={postalCodeObject.name}
                onPostalCodeChange={postalCodeDialog.enforceDialog}
                renderLabel={({ code, name }) => (
                  <Trans>
                    Predikce pro {code}, {name}
                  </Trans>
                )}
              >
                <Trans>
                  Nyní zobrazujeme predikce výnosů pro oblast dle poštovního
                  směrovacího čísla (PSČ)
                </Trans>
              </PostalCodeInfoBox>
              <Spacer className="h-6" />
              <Callout>
                <Trans>
                  <p className="text-justify">
                    <small>
                      V této části aplikace zobrazujeme predikovaný výnos
                      hlavních plodin v jednotlivých regionech České republiky.
                      Predikce vychází z kombinace dat družicového monitoringu
                      porostů, informací o půdní vlhkosti a aktuálních i
                      historických meteorologických údajů.
                    </small>
                  </p>
                  <p className="text-justify">
                    <small>
                      Uvedená data slouží především k odhadu možného objemu
                      produkce při prodeji budoucí sklizně, ke zlepšení
                      rozhodování v rostlinné výrobě a k včasné identifikaci
                      rizika poklesu výnosu aby bylo možné upravit agrotechnická
                      opatření nebo obchodní strategii.
                    </small>
                  </p>
                </Trans>
              </Callout>

              <Spacer className="h-6" />
              <div
                className={`flex flex-col gap-x-6 gap-y-8 md:mx-0 lg:grid ${sidebar.state.screen ? "lg:grid-cols-1 2xl:grid-cols-2" : "lg:grid-cols-2"}`}
              >
                {crops.map((crop) => (
                  <CropChart
                    crop={crop}
                    key={crop.id}
                    postalCode={params?.postalCode}
                  />
                ))}
              </div>
              {showCopyright ? (
                <p className="text-can-slate-gray text-center mt-6 text-xs">
                  <Trans>
                    Zdroj:{" "}
                    <a
                      className="text-can-forest-teal underline"
                      href="https://www.vynosy-plodin.cz"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      vynosy-plodin.cz
                    </a>
                  </Trans>
                </p>
              ) : null}
              <Spacer />
            </div>
            {params?.enforceDialog ? (
              <PostalCodeDialog
                submitButton={
                  <PostalCodeDialog.SubmitButton>
                    <Trans>Zobrazit predikce</Trans>
                  </PostalCodeDialog.SubmitButton>
                }
              >
                <Trans>
                  Pro zobrazení predikce výnosů vyplňte do pole níže poštovní
                  směrovací číslo (PSČ).
                </Trans>
              </PostalCodeDialog>
            ) : null}
          </>
        ),
        header: (
          <PageTitle>
            <Trans>Predikce výnosů</Trans>
          </PageTitle>
        ),
      }}
      right={false}
    />
  );
};

const CropChart = ({
  crop,
  postalCode,
}: {
  crop: CropConfig;
  postalCode?: string;
}) => {
  const { activeFilter, filters, onClick, selectedYear } = useYearFilters({
    cropId: crop.id,
  });

  return (
    <Chart.Container>
      <Chart.Title>{crop.label}</Chart.Title>
      <QuickFilters
        activeFilter={activeFilter}
        filters={filters}
        onClick={onClick}
      />
      <Chart.ChartContainer>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary
              fallbackRender={({ error, resetErrorBoundary }) => (
                <Failed error={error}>
                  <button
                    className="text-can-forest-teal text-xs underline"
                    onClick={resetErrorBoundary}
                  >
                    <Trans>Zkusit znovu</Trans>
                  </button>
                </Failed>
              )}
              onReset={reset}
            >
              <Suspense fallback={<Spinner />}>
                {postalCode ? (
                  <CropChartContent
                    cropId={crop.id}
                    postalCode={postalCode}
                    year={selectedYear}
                  />
                ) : (
                  <NoData>
                    <Trans>Zadejte PSČ pro zobrazení predikce</Trans>
                  </NoData>
                )}
              </Suspense>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </Chart.ChartContainer>
    </Chart.Container>
  );
};

const CropChartContent = ({
  cropId,
  postalCode,
  year,
}: {
  cropId: CropConfig["id"];
  postalCode: string;
  year: number;
}) => {
  const prediction = useQuery(predictionQuery(cropId, postalCode, year));

  if (prediction.status === "pending") {
    return <Spinner />;
  }

  if (prediction.status === "error") {
    return <Failed error={prediction.error} />;
  }

  if (!prediction.data.rates.length) {
    return (
      <NoData>
        <Trans>Pro tuto oblast nejsou k dispozici predikce výnosů</Trans>
      </NoData>
    );
  }

  return <PredictionChart data={prediction.data.rates} />;
};

export { path, YieldPredictions };
